import { useQuery } from "@tanstack/react-query";

import { useApiClient } from "~/hooks/use-api-client";
import { logger } from "~/lib/logger";

interface BatchStatus {
    clientId: string;
    sessionId: string | null;
    counts: {
        queued: number;
        inProgress: number;
        error: number;
    };
    partCount: number;
}

export function useBatchGenerateStatus({
    clientId,
    sessionId,
    enabled = true,
    refetchIntervalMs = 5000,
    forceRefetch = false,
}: {
    clientId: string;
    sessionId?: string;
    enabled?: boolean;
    refetchIntervalMs?: number;
    /** Keep polling even when no active jobs (e.g., waiting for Inngest to create records) */
    forceRefetch?: boolean;
}) {
    const api = useApiClient();

    return useQuery<BatchStatus, Error>({
        queryKey: ["batch-generate-status", clientId, sessionId ?? null],
        enabled: Boolean(enabled && clientId),
        // AIDEV-NOTE: Only poll when there are active jobs, unless forceRefetch is true
        // (used by MonitorGeneration to keep polling while waiting for Inngest)
        refetchInterval: (query) => {
            if (!enabled) return false;
            if (forceRefetch) return refetchIntervalMs;
            const data = query.state.data as BatchStatus | undefined;
            const hasActiveJobs =
                data && (data.counts.queued > 0 || data.counts.inProgress > 0);
            return hasActiveJobs ? refetchIntervalMs : false;
        },
        queryFn: async () => {
            const params = new URLSearchParams({ clientId });
            if (sessionId) params.set("sessionId", sessionId);
            const url = `/part/batch-generate-status?${params.toString()}`;

            const res = await api.get(url);
            if (!res.ok) {
                let details: { error?: string } | undefined;
                try {
                    details = await res.json();
                } catch {}
                const msg = details?.error || `HTTP ${res.status}`;
                throw new Error(msg);
            }

            const data = (await res.json()) as BatchStatus;
            return data;
        },
        throwOnError(error, query) {
            logger
                .withError(error)
                .error("Failed to fetch batch generate status");
            throw error;
        },
    });
}
